import Message from '@/component/Message'
import * as http from '@/util/http'
import { fetchSysConfig } from '@/util/sys'
import Editor from '@/widget/Editor'
import { $isImageNode } from '@/widget/Editor/node/ImageNode'
import { zodResolver } from '@hookform/resolvers/zod'
import { $dfs } from '@lexical/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { EditorState, LexicalEditor } from 'lexical'
import { useEffect, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import { useForm } from 'react-hook-form'
import { ArrowLeftIcon, XIcon } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import Select from 'react-select'
import {
  type CompanyUpsert,
  CompanyUpsertSchema,
  fetchCompany,
  type PresignedUrlResp,
  fetchIndustries,
  type Option,
  type FileUpdate,
} from './data'

export function CompanyEditor() {
  const navigate = useNavigate()
  // maybe we should use jotai atomic or get cache from react-query in the future
  const { id } = useParams<{ id: string }>()
  const companyId = id ? Number(id) : undefined
  const {
    data: company,
    // isLoading: loadingCompany,
    // isError: CompanyError,
  } = useQuery({
    queryKey: ['company', companyId || ''],
    queryFn: () => fetchCompany(companyId),
  })

  const {
    data: industryTags,
    // isLoading: industryCategoriesLoading,
    // isError: industryCategoriesError,
  } = useQuery<Option[]>({
    queryKey: ['industry-tag'],
    queryFn: fetchIndustries,
    staleTime: 1000 * 60 * 5,
  })

  const [selectedIndustryTags, setSelectedIndustryTags] = useState<Option[] | undefined>(
    company?.industries?.map((ind) => ({ label: ind.name, value: ind.id }))
  )

  const {
    data: sysConfig,
    isLoading: loadingSysConfig,
    isError: sysConfigError,
  } = useQuery({
    queryKey: ['sys-config'],
    queryFn: fetchSysConfig,
    staleTime: 1000 * 60 * 30,
  })
  const {
    register,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CompanyUpsertSchema),
    // defaultValues: company,
    mode: 'onChange',
  })

  const [summaryItem, setSummaryItem] = useState<string>('')
  const [summaryItems, setSummaryItems] = useState<string[]>([])

  useEffect(() => {
    if (!company) return
    reset(company)
    setSummaryItems(company?.summary ?? [])
  }, [company, reset])

  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: async (payload: CompanyUpsert): Promise<number> => {
      const { data } = await http.put<number>('/api/v1/company', { payload })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] })
    },
  })
  const editorRef = useRef<LexicalEditor | null>(null)
  function setEditorState(_editorState: EditorState, editor: LexicalEditor, _tags: Set<string>) {
    editorRef.current = editor
  }

  async function handleSave() {
    const id = companyId ?? (await mutation.mutateAsync({ ...getValues() }))
    const editor = editorRef.current
    const uploadTasks: Promise<void>[] = []

    const files: FileUpdate[] = []

    editor?.getEditorState().read(() => {
      $dfs().forEach(({ node }) => {
        if (!$isImageNode(node)) return

        const file = node.getFile()
        // saved before
        if (!file || !node.__src.startsWith('data:')) {
          files.push({ s3Key: node.getS3Key(), mediaType: 'IMG' })
          return
        }

        uploadTasks.push(
          (async () => {
            const {
              data: { url, key },
            } = await http.post<PresignedUrlResp>(`/api/v1/company/${id}/presigned-url`, {
              payload: {
                fileName: file.name,
                contentType: file.type,
                mediaType: 'IMG',
              },
            })

            await http.put(url, {
              credentials: 'same-origin',
              headers: { 'Content-Type': file.type },
              payload: file,
            })

            editor?.update(() => {
              node.setSrc(`https://${sysConfig?.cdnDomain}/${key}`)
              node.setS3Key(key)
              node.setFile(undefined)
            })

            files.push({
              mediaType: 'IMG',
              s3Key: key,
              fileName: file.name,
              fileSize: file.size,
              contentType: file.type,
            })
          })()
        )
      })
    })

    await Promise.all(uploadTasks)
    await mutation.mutateAsync({
      ...getValues(),
      intro: editor?.getEditorState().toJSON(),
      id,
      summary: summaryItems,
      industries: selectedIndustryTags?.map(({ label, value }) => ({ id: value, name: label })),
      files,
    })
    navigate(`/company/${id}/view`, { replace: true })
  }

  return (
    <>
      <div className="d-flex flex-row justify-content-between mb-4">
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center"
          onClick={() =>
            companyId ? navigate(`/company/${companyId}/view`, { replace: true }) : navigate(-1)
          }
        >
          <ArrowLeftIcon />
        </Button>
        <Button
          disabled={loadingSysConfig || sysConfigError}
          variant="outline-primary"
          onClick={handleSave}
        >
          保存
        </Button>
      </div>
      <FloatingLabel controlId="companyName" label="会社名" className="mb-3">
        <Form.Control
          type="text"
          placeholder="会社名"
          {...register('name')}
          isInvalid={!!errors.name}
        />
      </FloatingLabel>

      <FloatingLabel controlId="companyUrl" label="URL" className="mb-3">
        <Form.Control type="url" placeholder="URL" {...register('url')} isInvalid={!!errors.url} />
      </FloatingLabel>

      <Select
        options={industryTags}
        isMulti
        placeholder="業種タグ"
        closeMenuOnSelect={false}
        value={selectedIndustryTags}
        onChange={(opts) => {
          setSelectedIndustryTags([...opts])
        }}
        styles={{
          menu: (provided) => ({
            ...provided,
            zIndex: 9999,
          }),
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999,
          }),
        }}
      />

      <InputGroup className="my-3">
        <Form.Control
          placeholder="サマリー"
          aria-label="Summary item input"
          aria-describedby="button-add-summary-item"
          value={summaryItem}
          onChange={(e) => setSummaryItem(e.target.value)}
        />
        <Button
          variant="outline-secondary"
          id="button-add-summary-item"
          onClick={() => {
            setSummaryItems((summaryItems) => [...summaryItems, summaryItem])
            setSummaryItem('')
          }}
        >
          追加
        </Button>
      </InputGroup>

      <div className="mb-4">
        {summaryItems.map((item, i) => (
          <Message
            key={i}
            className="d-flex flex-row justify-content-between align-items-center my-1"
          >
            {item}
            <Button variant="outline-secondary" size="sm">
              <XIcon
                onClick={(e) => {
                  e.stopPropagation()
                  setSummaryItems(summaryItems.filter((_, idx) => idx !== i))
                }}
              />
            </Button>
          </Message>
        ))}
      </div>

      <Editor onChange={setEditorState} savedEditorState={company?.intro} />
    </>
  )
}
